// src/app/services/wallet-connector.service.ts
import { Injectable, NgZone } from '@angular/core';
import { BrowserProvider, ethers } from 'ethers';
import { firstValueFrom } from 'rxjs';
import { WalletService } from './wallet.service';   // <- votre service existant

export interface WalletConnection {
  address: string;               // 0x…
  provider: BrowserProvider;     // wrapper ethers v6
  rawProvider: any;              // provider EIP-1193 brut (MetaMask ou WC)
  chainId: number;               // réseau courant (ex. 1, 137…)
}

@Injectable({ providedIn: 'root' })
export class WalletConnectorService {

  private current?: WalletConnection;

  constructor(
    private wallet: WalletService,
    private zone: NgZone         // pour repasser dans l’Angular zone sur les events
  ) { }

  /**————————————————————————————————————————————
   * Demande (ou restaure) la connexion de l’utilisateur.
   * Renvoie toujours le même objet tant que la page n’est
   * pas rechargée.
   *————————————————————————————————————————————*/
  async requestConnection(): Promise<WalletConnection> {

    // 1)  Déjà connecté ➜ on renvoie l’instance existante
    if (this.current) return this.current;

    // 2)  Demande la connexion via WalletService
    const address = await this.wallet.connect();        // MetaMask ou WC

    localStorage.setItem('wallet', address);   // AJOUT

    // 3)  Récupère le provider brut
    const raw =
      this.wallet.getRawProvider?.()
      ?? (this.wallet as any).wcProvider
      ?? (this.wallet as any).wc?.provider;

    if (!raw) throw new Error('Raw provider WC manquant');

    // 4)  Wrap ethers v6
    const provider = new ethers.BrowserProvider(raw);

    // 5)  Chaîne courante
    const { chainId } = await provider.getNetwork();
    const chainIdNum = Number(chainId);

    // 6)  Écoute les changements de réseau
    raw.on?.('chainChanged', (id: string | number | bigint) => {
      const num = typeof id === 'string' ? parseInt(id, 16) : Number(id);
      // on repasse dans la zone Angular pour rafraîchir l’UI
      this.zone.run(() => {
        if (this.current) this.current.chainId = num;
      });
    });

    // 7)  Stocke et renvoie
    this.current = { address, provider, rawProvider: raw, chainId: chainIdNum };
    return this.current;
  }

  /** Permet de se déconnecter proprement partout. */
  async disconnect() {
    await this.wallet.disconnect();
    this.current = undefined;
  }
}
